// /notifications/:id — one notification in full (T24).
//
// Where a click lands when the notification has no record to open (and the
// way back to a linked one): full title and message, when it came, its type,
// and its data as readable rows, with 'Open record' when a link exists.
// Opening it marks it read. Another user's id is a 404 from the API.
// SHARED vertical. No feature flag. Min role: CASHIER (5) — all users.

import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageContainer, Button, Skeleton, EmptyState, ErrorBoundary } from '@bbpos/ui';
import { notificationService } from '@/services/notification.service';
import type { NotificationType } from '@/services/notification.service';
import { ApiError } from '@/lib/api-client';
import { GLASS, cn } from '@/constants/design-tokens';
import { useStoreTimezone } from '@/lib/store-clock';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/constants/product';
import { TYPE_META, relativeTime } from './NotificationCardBody';
import { TYPE_LABELS } from './NotificationCenterPage';
import {
  NOTIFICATIONS_QUERY_KEY,
  formatDateTime,
  humanize,
  notificationLink,
  readableDataFields,
} from './notification-links';

function BellOffIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0M3 3l18 18" />
    </svg>
  );
}

function NotificationDetailContent() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const timeZone = useStoreTimezone();
  const currency = useTenantCurrency();
  const roleLevel = useAuthStore((s) => ROLE_LEVELS[s.profile?.role ?? ''] ?? 0);

  const { data: n, isLoading, error } = useQuery({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, 'detail', id],
    queryFn: () => notificationService.get(id),
    enabled: Boolean(id),
    retry: false,
  });

  // Opening it is reading it. Once per id — the refetch after the
  // invalidation comes back read and must not mark again.
  const markedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!n || n.is_read || markedRef.current === n.id) return;
    markedRef.current = n.id;
    notificationService
      .markAsRead(n.id)
      .then(() => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY }))
      .catch(() => { /* the detail still reads fine; the list can mark it */ });
  }, [n, queryClient]);

  useEffect(() => {
    document.title = `${n?.title ?? 'Notification'} — Orio360 Admin`;
  }, [n?.title]);

  const breadcrumbs = [
    { label: 'Home', href: '/home' },
    { label: 'Notifications', href: '/notifications' },
    { label: n?.title ?? 'Notification' },
  ];

  if (isLoading) {
    return (
      <PageContainer title="Notification" breadcrumbs={breadcrumbs}>
        <div className={cn(GLASS, 'p-6 space-y-3')}>
          <Skeleton variant="text" width="30%" height={14} />
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="90%" height={14} />
          <Skeleton variant="text" width="80%" height={14} />
        </div>
      </PageContainer>
    );
  }

  if (error || !n) {
    const notFound = error instanceof ApiError && error.statusCode === 404;
    return (
      <PageContainer title="Notification" breadcrumbs={breadcrumbs}>
        <EmptyState
          variant="page"
          icon={<BellOffIcon />}
          title={notFound ? 'Notification not found' : 'Could not load this notification'}
          description={notFound
            ? "It may have been removed, or it isn't yours to see."
            : error instanceof Error ? error.message : 'Please try again.'}
          action={{ label: 'Back to notifications', onClick: () => navigate('/notifications') }}
        />
      </PageContainer>
    );
  }

  const meta = TYPE_META[n.type] ?? TYPE_META.SYSTEM_ANNOUNCEMENT;
  const typeLabel = TYPE_LABELS[n.type as NotificationType] ?? humanize(String(n.type));
  // No 'Open record' for a page the reader's role can't open (a cashier's bill link).
  const link = notificationLink(n, roleLevel);
  const fields = readableDataFields(n.metadata, { currency, timeZone });
  const created = new Date(n.created_at);

  return (
    <PageContainer
      title="Notification"
      subtitle={typeLabel}
      breadcrumbs={breadcrumbs}
      actions={
        <Button variant="secondary" size="sm" onClick={() => navigate('/notifications')}>
          All notifications
        </Button>
      }
    >
      <div className="animate-fade-in space-y-4 max-w-3xl">
        <div className="bg-gradient-to-r from-sky-600 to-indigo-600 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
          <div className="relative flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shrink-0">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d={meta.iconPath} />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/20">{typeLabel}</span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/10">
                  {n.is_read ? 'Read' : 'Unread'}
                </span>
              </div>
              <h1 className="text-xl font-bold break-words">{n.title}</h1>
              <p className="text-sky-100 text-sm mt-1 tabular-nums">
                {formatDateTime(created, timeZone)} · {relativeTime(n.created_at)}
              </p>
            </div>
          </div>
        </div>

        <div className={cn(GLASS, 'p-6 animate-slide-up')}>
          <h2 className="text-xs font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">
            Message
          </h2>
          <p className="text-sm text-neutral-800 dark:text-neutral-100 whitespace-pre-line break-words">
            {n.message}
          </p>
          {link && (
            <div className="mt-5">
              <Button variant="primary" size="sm" onClick={() => navigate(link)}>
                Open record
              </Button>
            </div>
          )}
        </div>

        {fields.length > 0 && (
          <div className={cn(GLASS, 'p-6 animate-slide-up')}>
            <h2 className="text-xs font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">
              Details
            </h2>
            <dl className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {fields.map((f) => (
                <div
                  key={f.key}
                  className="grid grid-cols-3 gap-3 py-2 px-2 -mx-2 rounded-lg transition-colors duration-200 hover:bg-brand-50/60 dark:hover:bg-brand-900/20"
                >
                  <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400">{f.label}</dt>
                  <dd className="col-span-2 text-sm text-neutral-900 dark:text-white break-words">
                    {f.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>
    </PageContainer>
  );
}

export function NotificationDetailPage() {
  return (
    <ErrorBoundary>
      <NotificationDetailContent />
    </ErrorBoundary>
  );
}
